import { escapeUserText } from "../../utils/mentions";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, Camera, ChevronRight, MapPin, Star } from "lucide-react";
import Header from "../headers/headerfornews";
import { EventMediaDisplay } from "./eventMediaDisplay";
import "./Alleventlist.css";

const createLinkifiedText = (text = "") =>
  escapeUserText(text).replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

const formatEventDate = (value) => {
  if (!value) return "TBA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatLocation = (event) => {
  const location = (event.location || "").trim();
  const country = (event.country || "").trim();
  if (location && country && location.toLowerCase() !== country.toLowerCase()) {
    return `${location}, ${country}`;
  }
  return location || country || "TBA";
};

const resolveMediaSrc = (value, apiUrl) => {
  if (!value) return "";
  if (value.startsWith("//")) return `https:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiUrl}/uploads/events/${value.replace(/^\/+/, "")}`;
};

const parseMediaItems = (event, apiUrl) => {
  if (!event?.media) return [];

  let raw = event.media;
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      raw = JSON.parse(raw);
    } catch {
      /* keep string */
    }
  }

  const parts = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

  if (parts.length <= 1) {
    return event.media
      ? [
          {
            src: resolveMediaSrc(event.media, apiUrl),
            type: event.media_type || "image",
          },
        ]
      : [];
  }

  return parts.map((part) => ({
    src: resolveMediaSrc(part, apiUrl),
    type: /\.(mp4|webm|mov|m4v)(\?|$)/i.test(part) ? "video" : "image",
  }));
};

const truncateText = (text = "", limit = 140) => {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};

const EventMediaPanel = ({ event, apiUrl }) => {
  const items = parseMediaItems(event, apiUrl);
  const imageItems = items.filter((item) => item.type === "image");
  const showGrid = imageItems.length >= 4;

  if (!event?.media) {
    return <div className="ea-media-empty">No media</div>;
  }

  if (showGrid) {
    return (
      <>
        <div className="ea-media-grid">
          {imageItems.slice(0, 4).map((item, index) => (
            <img
              key={`${item.src}-${index}`}
              src={item.src}
              alt={`${event.event_name || "Event"} ${index + 1}`}
            />
          ))}
        </div>
        <span className="ea-photo-badge">
          <Camera size={14} strokeWidth={2.4} />
          {imageItems.length} Photos
        </span>
      </>
    );
  }

  return (
    <>
      <div className="ea-media-inner">
        <EventMediaDisplay
          event={event}
          apiUrl={apiUrl}
          mediaStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
          noMediaContent={<div className="ea-media-empty">No media</div>}
        />
      </div>
      {event.media_type === "image" && (
        <span className="ea-photo-badge">
          <Camera size={14} strokeWidth={2.4} />
          1 Photo
        </span>
      )}
    </>
  );
};

const AllEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    message: "",
  });

  const API_URL = process.env.REACT_APP_API_URL;
  const cardsRef = useRef([]);

  useEffect(() => {
    fetch(`${API_URL}/get_event_activities.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setEvents(data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        setLoading(false);
      });
  }, [API_URL]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-id");
          if (entry.isIntersecting) {
            setVisibleCards((prev) => ({ ...prev, [id]: true }));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [events]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.contact || !formData.message) {
      alert("Please fill out all fields.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/contact_submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        alert("Thank you for reaching out! We’ll get back to you soon.");
        setFormData({ name: "", email: "", contact: "", message: "" });
        setShowContactModal(false);
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      console.error("Contact form submission failed:", error);
      alert("Something went wrong. Please try again later.");
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <p className="ea-loading">Loading events...</p>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="ea-page">
        <header className="ea-header">
          <div className="ea-header-icon" aria-hidden="true">
            <Calendar size={22} strokeWidth={2.2} />
          </div>
          <h2 className="ea-title">All Event Activities</h2>
          <p className="ea-subtitle">
            Discover and explore our latest events and activities
          </p>
          <div className="ea-header-rule" />
        </header>

        {events.length === 0 ? (
          <p className="ea-empty">No events found.</p>
        ) : (
          <div className="ea-list">
            {events.map((event, index) => {
              const isFeatured = index === 0;
              const isVisible = visibleCards[event.id];
              const description = truncateText(event.description || "", 160);

              return (
                <article
                  key={event.id}
                  data-id={event.id}
                  ref={(el) => {
                    cardsRef.current[index] = el;
                  }}
                  className={`ea-card${isVisible ? " is-visible" : ""}`}
                >
                  <div className="ea-media">
                    <EventMediaPanel event={event} apiUrl={API_URL} />
                  </div>

                  <div className="ea-body">
                    <span className="ea-badge">
                      {isFeatured ? (
                        <>
                          <Star size={13} strokeWidth={2.4} fill="currentColor" />
                          Featured Event
                        </>
                      ) : (
                        <>
                          <Calendar size={13} strokeWidth={2.4} />
                          Open Enrollment
                        </>
                      )}
                    </span>

                    <h3 className="ea-event-title">{event.event_name}</h3>

                    <p
                      className="ea-desc"
                      dangerouslySetInnerHTML={{
                        __html: createLinkifiedText(description),
                      }}
                    />

                    <hr className="ea-divider" />

                    <div className="ea-meta">
                      <div className="ea-meta-row">
                        <span className="ea-meta-icon" aria-hidden="true">
                          <Calendar size={16} strokeWidth={2.2} />
                        </span>
                        <div className="ea-meta-copy">
                          <span className="ea-meta-label">Date</span>
                          <span className="ea-meta-value">
                            {formatEventDate(event.event_date)}
                          </span>
                        </div>
                      </div>

                      <div className="ea-meta-row">
                        <span className="ea-meta-icon" aria-hidden="true">
                          <MapPin size={16} strokeWidth={2.2} />
                        </span>
                        <div className="ea-meta-copy">
                          <span className="ea-meta-label">Location</span>
                          <span className="ea-meta-value">
                            {formatLocation(event)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ea-actions">
                      <Link
                        to={`/events/${event.id}`}
                        className={`ea-btn ${
                          isFeatured ? "ea-btn-primary" : "ea-btn-outline"
                        }`}
                      >
                        View Details
                        <ChevronRight size={16} strokeWidth={2.5} />
                      </Link>
                      <button
                        type="button"
                        className="ea-btn ea-btn-outline"
                        onClick={() => setShowContactModal(true)}
                      >
                        Contact Us
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <p className="ea-footer">
          <Calendar size={15} strokeWidth={2} />
          More events and activities coming your way. Stay tuned and don&apos;t
          miss out!
        </p>
      </div>

      {showContactModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 16,
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.2)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", color: "#0f2744" }}>Contact Us</h3>
            <form
              onSubmit={handleSubmitContact}
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                style={modalFieldStyle}
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                style={modalFieldStyle}
              />
              <input
                type="text"
                name="contact"
                placeholder="Contact Number"
                value={formData.contact}
                onChange={handleInputChange}
                style={modalFieldStyle}
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                style={{ ...modalFieldStyle, minHeight: 80 }}
              />
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setShowContactModal(false)}
                  style={{
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: "#e2e8f0",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: 8,
                    background: "#2f6bff",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const modalFieldStyle = {
  padding: 10,
  border: "1px solid #dbe3ef",
  borderRadius: 8,
  fontFamily: "inherit",
};

export default AllEvents;
